import { ShoppingCart, ArrowRight, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import type { FlightResult, Flight, MultiSliceFlight, FareOption, FlightDetails } from "@/types/travel";
import { formatCurrency, formatTime } from "@/lib/utils";

interface BookingConfirmationProps {
  flight: FlightResult;
  selectedFare: FareOption;
  flightDetails: FlightDetails;
  onAddToCart: () => void;
  onCheckout: () => void;
}

export function BookingConfirmation({
  flight,
  selectedFare,
  flightDetails,
  onAddToCart,
  onCheckout,
}: BookingConfirmationProps) {
  const isMultiSlice = (flight as MultiSliceFlight).type === "multi-slice";
  const firstSlice = isMultiSlice
    ? (flight as MultiSliceFlight).slices[0]
    : (flight as Flight);
  const lastSlice = isMultiSlice
    ? (flight as MultiSliceFlight).slices[(flight as MultiSliceFlight).slices.length - 1]
    : (flight as Flight);

  return (
    <div className="space-y-6">
      {/* Flight Summary */}
      <Card className="p-4">
        <h4 className="font-semibold mb-3">Flight Summary</h4>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="font-medium">
                {firstSlice.origin} → {lastSlice.destination}
              </p>
              <p className="text-sm text-muted-foreground">
                {formatTime(firstSlice.departureTime)} - {formatTime(lastSlice.arrivalTime)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">{firstSlice.airline}</p>
              <p className="text-sm text-muted-foreground">Flight {firstSlice.flightNumber}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Selected Fare */}
      <Card className="p-4">
        <h4 className="font-semibold mb-3">Selected Fare: {selectedFare.name}</h4>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Changes:</span>
              <p className="font-medium">{selectedFare.included.changePolicy}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Refunds:</span>
              <p className="font-medium">{selectedFare.included.refundPolicy}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Carry-on:</span>
              <p className="font-medium">{selectedFare.included.carryOnBags}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Checked bags:</span>
              <p className="font-medium">{selectedFare.included.checkedBags || 'Not included'}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Environmental Impact */}
      {flightDetails.co2Emissions && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
          <Leaf className="h-4 w-4 text-green-600 dark:text-green-400" />
          <span className="text-sm text-green-700 dark:text-green-300">
            CO₂ emissions: {flightDetails.co2Emissions}
          </span>
        </div>
      )}

      <Separator />

      {/* Price Summary */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-muted-foreground">Base fare</span>
          <span>{formatCurrency(selectedFare.price)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-muted-foreground">Taxes & fees</span>
          <span className="text-sm text-muted-foreground">Included</span>
        </div>
        <Separator />
        <div className="flex justify-between items-center">
          <span className="font-semibold text-lg">Total</span>
          <span className="font-semibold text-lg">{formatCurrency(selectedFare.price)}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1 flex items-center justify-center gap-2"
          onClick={onAddToCart}
        >
          <ShoppingCart className="h-4 w-4" />
          Add to Cart
        </Button>
        <Button
          variant="default"
          className="flex-1 flex items-center justify-center gap-2"
          onClick={onCheckout}
        >
          Go to checkout
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}